// src/components/providerAssignments/CircleProvidersTab.tsx
//
// Circle → Providers tab — READ-ONLY view of the effective provider
// catalog for a Circle.
//
// PRODUCT MODEL (locked):
//   Circle → Providers  = Outbound-synced WorkspaceProvider catalog,
//                         mirrored into CRM via IntegrationConnection
//                         rows where `settings.sync.source === 'outbound'`.
//                         Provider CONFIGURATION lives in Outbound
//                         (surfaced through CRM Settings → Providers).
//                         This tab is DISPLAY-ONLY: no Add, no Remove,
//                         no mutation UI.
//   Circle → Assignments = PersonProviderAssignment relationships
//                         (Contact ↔ workspace User ↔ ProviderRole).
//                         Owned by CircleAssignmentsTab (unchanged).
//   Circle → People     = Central TeamMembership users (unchanged).
//
// This tab MUST NOT:
//   - construct rows from PersonProviderAssignment,
//   - fabricate assignment counts against outboundProviderId,
//   - expose credentials beyond the last-4 masks Outbound already
//     stamped into the mirror,
//   - offer any Circle-affinity mutations (Add / Remove) — those still
//     live on the API but are not exposed from this surface,
//   - query Central, Contacts, or any per-user surface.
//
// The `Manage providers` link is preserved as a navigation-only
// affordance into the workspace-level Settings → Providers page.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useGetCircleProvidersQuery } from '../../api/circlesApi';
import {
  expandVisibleProviderRows,
  type ExpandedProviderRow,
} from '../../components/providers/providerVisibility';
import type { Circle, IntegrationConnection } from '../../types/api';

export interface CircleProvidersTabProps {
  circle: Circle;
  workspaceId?: string;
}

export default function CircleProvidersTab({
  circle,
  workspaceId,
}: CircleProvidersTabProps) {
  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useGetCircleProvidersQuery(circle.id, {
    skip: !workspaceId || !circle.id,
    refetchOnMountOrArgChange: 30,
  });

  const integrations = data?.integrations ?? [];
  const hasExplicitAffinity = data?.affinity?.hasExplicitAffinity ?? false;

  // Parametrize on the CRM's `IntegrationConnection` (which carries the
  // wider `accountMode` / `lastSyncedAt` / `updatedAt` fields on top of
  // the shared package's minimal interface) so the row cells below can
  // reach those columns without a widening cast.
  const rows: ExpandedProviderRow<IntegrationConnection>[] = useMemo(
    () => expandVisibleProviderRows<IntegrationConnection>(integrations),
    [integrations],
  );

  return (
    <section data-testid="circle-providers-tab" className="space-y-4">
      <Header />

      {!isLoading && !isError && !hasExplicitAffinity && (
        <div
          role="note"
          data-testid="circle-providers-backward-compat-notice"
          className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900"
        >
          All workspace providers are currently available to this Circle.
        </div>
      )}

      {isLoading && (
        <p className="text-xs text-slate-500" data-testid="circle-providers-loading">
          Loading providers…
        </p>
      )}

      {isError && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
          data-testid="circle-providers-error"
        >
          <p>Failed to load providers.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-xs font-medium underline underline-offset-2"
            data-testid="circle-providers-retry"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <div
          className="rounded-md border border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500"
          data-testid="circle-providers-empty"
        >
          {hasExplicitAffinity
            ? 'No providers are scoped to this Circle yet.'
            : 'No providers are currently synced from Outbound for this workspace.'}
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="min-w-full text-xs">
            <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
              <tr>
                <Th>Provider</Th>
                <Th>Type</Th>
                <Th>Vendor</Th>
                <Th>Mode</Th>
                <Th>Status</Th>
                <Th>Sender</Th>
                <Th>Credentials</Th>
                <Th className="text-right">Last synced</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <ProviderRow key={row.rowKey} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function Header() {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Providers</h3>
        <p className="text-xs text-slate-500">
          Communication and service providers synced from LevelUp Outbound.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Provider configuration is managed through LevelUp Outbound; this tab
          is read-only.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {/* Navigation-only link into the canonical Settings surface for
            full provider configuration. Does not mutate this Circle. */}
        <Link
          to="/settings/providers"
          className="text-xs font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700"
          data-testid="circle-providers-manage-link"
        >
          Manage providers
        </Link>
      </div>
    </div>
  );
}

function ProviderRow({
  row,
}: {
  row: ExpandedProviderRow<IntegrationConnection>;
}) {
  const testId = `circle-provider-row-${row.rowKey}`;
  const displayName =
    row.channel.displayName || row.integration.displayName || row.integration.appKey || 'Provider';
  const statusLabel = row.channel.status || row.integration.status || 'unknown';
  const sender = senderFor(row);
  const credentials = credentialSummaryFor(row);
  const lastSynced = row.integration.lastSyncedAt
    ? new Date(row.integration.lastSyncedAt).toLocaleString()
    : row.integration.updatedAt
      ? new Date(row.integration.updatedAt).toLocaleString()
      : '—';

  return (
    <tr className="hover:bg-slate-50" data-testid={testId}>
      <Td className="font-medium text-slate-900">{displayName}</Td>
      <Td className="text-slate-700">{formatChannelType(row.channel.type)}</Td>
      <Td className="text-slate-700">{row.integration.appKey}</Td>
      <Td className="text-slate-700">{row.integration.accountMode ?? '—'}</Td>
      <Td>
        <StatusPill status={statusLabel} />
      </Td>
      <Td className="text-slate-700">{sender}</Td>
      <Td className="font-mono text-slate-500">{credentials}</Td>
      <Td className="text-right text-slate-500">{lastSynced}</Td>
    </tr>
  );
}

function StatusPill({ status }: { status: string }) {
  const s = status.toLowerCase();
  const tone =
    s === 'connected' || s === 'active'
      ? 'bg-green-50 text-green-700 ring-green-200'
      : s === 'suspended' || s === 'disconnected' || s === 'error'
        ? 'bg-red-50 text-red-700 ring-red-200'
        : 'bg-slate-100 text-slate-700 ring-slate-200';
  const label =
    s === 'connected' ? 'Active' : s === 'disconnected' ? 'Inactive' : s.charAt(0).toUpperCase() + s.slice(1);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${tone}`}
    >
      {label}
    </span>
  );
}

function senderFor(row: ExpandedProviderRow<IntegrationConnection>): string {
  const s = row.channel.senderIdentity;
  if (!s) return '—';
  return s.fromNumber || s.fromEmail || s.fromName || '—';
}

function credentialSummaryFor(row: ExpandedProviderRow<IntegrationConnection>): string {
  const c = row.channel.credentialMask;
  if (!c) return '—';
  const parts: string[] = [];
  if (c.sidLast4) parts.push(`SID ••••${c.sidLast4}`);
  if (c.keyLast4) parts.push(`KEY ••••${c.keyLast4}`);
  if (c.secretLast4) parts.push(`SEC ••••${c.secretLast4}`);
  return parts.length ? parts.join(' · ') : '—';
}

function formatChannelType(t: string | undefined): string {
  if (!t) return '—';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={`px-3 py-2 text-left font-medium uppercase tracking-wider ${className ?? ''}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`whitespace-nowrap px-3 py-2 ${className ?? ''}`}>{children}</td>;
}
